import { Dispatch, FC, SetStateAction, useState } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
	BlockquotePlugin,
	BoldPlugin,
	CodePlugin,
	ItalicPlugin,
	StrikethroughPlugin,
	UnderlinePlugin,
} from '@platejs/basic-nodes/react';
import { BaseCodeBlockPlugin } from '@platejs/code-block';

import { Button, Carousel, Flex, GetProp, notification, Typography, Upload, type UploadFile, UploadProps } from 'antd';
import { useFormik } from 'formik';
import { Value } from 'platejs';
import { usePlateEditor } from 'platejs/react';
import { Plate } from 'platejs/react';

import ClipIcon from 'assets/images/icons/clip.svg?react';
import DeleteIcon from 'assets/images/icons/delete.svg?react';
import { type OfferRead } from 'entities/Brand';
import { EditPostPageState } from 'entities/Post';
import {
	AffiliateLinkPlugin,
	BlockquoteElement,
	CodeBlockElement,
	CodeElement,
	EmojiKit,
	FixedToolbarKit,
	FloatingToolbarKit,
	LinkKit,
	SpoilerPlugin,
} from 'features/Editor';
import { Editor, EditorContainer, editorToHtml } from 'features/Editor';
import { Content } from 'shared/ui';

import styles from '../PostForm.module.scss';

type TFileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

type TPreviewFileType = TFileType & { preview: string };

const MAX_IMG_SIZE = 5 * 1024 * 1024;

const getBase64 = (file: TFileType): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = (error) => reject(error);
	});

interface IPostEditorPartProps extends Pick<ReturnType<typeof useFormik>, 'setFieldValue' | 'values'> {
	offer: OfferRead;
	setPageState: Dispatch<SetStateAction<EditPostPageState>>;
	setEditingTime: Dispatch<SetStateAction<Date | null>>;
	plateValue: Value | undefined;
	setPlateValue: Dispatch<SetStateAction<Value | undefined>>;
}

export const PostEditorPart: FC<IPostEditorPartProps> = ({
	offer,
	setFieldValue,
	values,
	setPageState,
	setEditingTime,
	plateValue,
	setPlateValue,
}) => {
	const { t } = useTranslation();
	const affiliateLinks = offer.affiliate_links.filter((item) => values.channel_ids.includes(item.channel.uuid));
	const [error, setError] = useState<string | null>(null);
	const [currentSlide, setCurrentSlide] = useState<number>();

	const handleDeleteClick = () => {
		const newFileList = values.images.concat();
		newFileList.splice(currentSlide, 1);
		setFieldValue('images', newFileList);
	};

	const handleBeforeUpload = async (file: UploadFile) => {
		setError(null);
		if (!file.url && !file.preview) {
			const isLt5M = (file.size || 0) < MAX_IMG_SIZE;
			if (!isLt5M) {
				setError(t('Изображение должно быть меньше чем 5MB'));
			} else {
				file.preview = await getBase64(file as TFileType);
			}
		}
		return false;
	};

	const onChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
		setFieldValue('images', newFileList);
	};

	const handleCarouselChange = (currentSlide: number) => {
		setCurrentSlide(currentSlide);
	};

	const editor = usePlateEditor({
		plugins: [
			BoldPlugin,
			ItalicPlugin,
			UnderlinePlugin,
			StrikethroughPlugin,
			CodePlugin.withComponent(CodeElement),
			BaseCodeBlockPlugin.withComponent(CodeBlockElement),
			SpoilerPlugin,
			AffiliateLinkPlugin,
			BlockquotePlugin.withComponent(BlockquoteElement),
			...EmojiKit,
			...FloatingToolbarKit,
			...FixedToolbarKit,
			...LinkKit,
		],
	});

	const [initialPlateValue, setInitialPlateValue] = useState(plateValue);

	useEffect(() => {
		if (plateValue) {
			setInitialPlateValue(plateValue);
			editor.tf.setValue(plateValue);
		} else {
			const slateValue = editor.api.html.deserialize({ element: values.content, collapseWhiteSpace: false });
			setInitialPlateValue([{ children: slateValue, type: 'none' }]);
			editor.tf.setValue([{ children: slateValue, type: 'none' }]);
		}
	}, []); //eslint-disable-line react-hooks/exhaustive-deps

	const handleBackClick = () => {
		editor.tf.setValue(initialPlateValue);
		setFieldValue('content', editorToHtml(initialPlateValue));
	};

	const handleSaveClick = () => {
		if (!values.content.includes('{affiliate_link}')) {
			notification.error({
				message: t('Текст поста должен содержать партнерскую ссылку'),
				placement: 'bottom',
				showProgress: true,
				pauseOnHover: true,
			});
			return;
		}

		setPageState(EditPostPageState.PUBLISHING_SETTINGS);
	};

	const onContentChange = async ({ value }: { value: Value }) => {
		//console.log(value);
		//console.log(editorToHtml(value));

		setPlateValue(value);
		setFieldValue('content', editorToHtml(value));
		setEditingTime(new Date());
	};

	return (
		<>
			<Content>
				<Flex vertical gap={8}>
					<div className={styles.postForm__card}>
						<Typography.Title level={5} className={styles.postForm__m0}>
							{t('Иллюстрация')}
						</Typography.Title>

						{values.images?.length ? (
							<Carousel
								className={styles.postForm__carousel}
								afterChange={handleCarouselChange}
								dots={values.images?.length > 1}>
								{values.images.map((img: TPreviewFileType) => (
									<img key={img.uid} src={img.preview} alt={img.name} className={styles.postForm__ill} />
								))}
							</Carousel>
						) : offer?.posts?.[0]?.images?.[0]?.image ? (
							<img src={offer?.posts[0].images[0].image} alt="" className={styles.postForm__ill} />
						) : null}

						<Flex gap={8}>
							<Button
								onClick={handleDeleteClick}
								htmlType="button"
								size="large"
								icon={<DeleteIcon />}
								variant="outlined"
								color="danger"
								className={styles.postForm__deleteBtn}
								disabled={!values.images?.length}
							/>

							<Upload
								className={styles.postForm__upload}
								beforeUpload={handleBeforeUpload}
								onChange={onChange}
								showUploadList={false}
								fileList={values.images}
								accept={'image/png, image/jpeg, image/jpg, image/gif'}>
								<Button htmlType="button" size="large" icon={<ClipIcon />} block>
									{t('Добавить изображение')}
								</Button>
								<Typography.Text type="danger">{error}</Typography.Text>
							</Upload>
						</Flex>
					</div>
					<div className={styles.postForm__card}>
						<Typography.Title level={5}>{t('Текст поста')}</Typography.Title>
						<Plate editor={editor} onChange={onContentChange}>
							<EditorContainer>
								<Editor placeholder={t('Текст поста...')} />
							</EditorContainer>
						</Plate>
					</div>
					<div className={styles.postForm__card}>
						<Typography.Title level={5}>{t('Партнерская ссылка')}</Typography.Title>
						{affiliateLinks.map((affiliateLink) => (
							<div key={affiliateLink.channel.uuid}>
								<Typography.Text code underline copyable>
									{affiliateLink.affiliate_link}
								</Typography.Text>
							</div>
						))}
					</div>
				</Flex>
			</Content>
			<Flex justify="space-between" gap={8} className={styles.postForm__footer}>
				<Button htmlType="button" size="large" onClick={handleBackClick}>
					{t('Отменить изменения')}
				</Button>
				<Button htmlType="button" size="large" type="primary" onClick={handleSaveClick} disabled={!values.content}>
					{t('Сохранить')}
				</Button>
			</Flex>
		</>
	);
};
